// What the Catalog Item Image Storage box shows while an admin edits the folder link. The link is checked
// against SharePoint as it is typed (after a pause, or on leaving the box), and the answer only ever describes
// the text it was asked about - so an answer for text since edited is hidden rather than shown against the new
// text, and an edit clears the error at once instead of leaving it up until the next check returns.
//
// Saving is the server's job: it resolves the link once and keeps the folder by its ids, so a "Copy link" link
// that later expires does not matter. The box then shows the folder's own address.

export type FolderCheck =
  | { url: string; status: 'checking' }
  | { url: string; status: 'found'; location: string; webUrl?: string }
  | { url: string; status: 'notFound' }
  | { url: string; status: 'unreachable' }
  | { url: string; status: 'failed' };

export type FolderLinkView = {
  checking: boolean;
  notFound: boolean;
  unreachable: boolean;
  location?: { text: string; webUrl?: string; saved: boolean };
  blocksSave: boolean;
};

/** Where pictures are stored as saved, from `GET catalogimages/folder/current` or a save. */
export type SavedFolder = { url: string; found: boolean; location?: string; webUrl?: string };

export const FOLDER_CHECK_DELAY_MS = 600;

const NOTHING: FolderLinkView = { checking: false, notFound: false, unreachable: false, blocksSave: false };

/**
 * A 404 for a pasted link means the folder is wrong. A 404 for an EMPTY box only means the tenant has no default
 * team yet, which is not the admin's typo, so it is not shown as one - and nor is any other failure while typing,
 * which says nothing about the link.
 */
export const folderCheckFailure = (url: string, status?: number): FolderCheck =>
  url && status === 404 ? { url, status: 'notFound' } : { url, status: 'failed' };

/** A save that did not go through: a 404 is the link's fault, anything else is SharePoint not answering. */
export const folderSaveFailure = (url: string, status?: number): FolderCheck =>
  status === 404 ? { url, status: 'notFound' } : { url, status: 'unreachable' };

/** The saved folder as a check of the text now in the box. A chosen folder that has since gone reads as not found. */
export const savedFolderCheck = (saved: SavedFolder): FolderCheck =>
  saved.found
    ? { url: saved.url, status: 'found', location: saved.location ?? '', webUrl: saved.webUrl }
    : folderCheckFailure(saved.url, 404);

/**
 * Whether the box holds a different folder from the saved one. Spaces around the link are not a change: the save
 * trims them and sends nothing, so counting them left the page dirty after every save.
 */
export const folderLinkChanged = (typed: string, saved: string) => (typed ?? '').trim() !== (saved ?? '').trim();

export const folderLinkView = (typed: string, saved: string, check?: FolderCheck): FolderLinkView => {
  const url = typed.trim();
  if (check?.url !== url) return NOTHING;
  // Only a link being changed holds Save. The saved folder having gone must not stop the rest of the page saving.
  const changed = folderLinkChanged(typed, saved);

  switch (check.status) {
    case 'checking':
      return { ...NOTHING, checking: true, blocksSave: changed };
    case 'notFound':
      return { ...NOTHING, notFound: true, blocksSave: changed };
    case 'unreachable':
      return { ...NOTHING, unreachable: true };
    case 'found':
      return { ...NOTHING, location: { text: check.location, webUrl: check.webUrl, saved: !changed } };
    default:
      return NOTHING;
  }
};
